import { useEffect, useRef, useState } from 'react';

const THRESHOLD = 80;
const MAX_PULL = 120;

function getScrollParent(node: HTMLElement | null): HTMLElement | null {
    if (!node) return null;
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === 'scroll' || overflowY === 'auto') return node;
    return getScrollParent(node.parentElement);
}

function usePullToRefresh(onRefresh: () => Promise<unknown> | void) {
    const [pullDistance, setPullDistance] = useState(0);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const startYRef = useRef(0);
    const pullDistanceRef = useRef(0);
    const isRefreshingRef = useRef(false);
    const isDraggingRef = useRef(false);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return () => {};
        const scrollParent = getScrollParent(el) ?? document.documentElement;

        const onTouchStart = (e: TouchEvent) => {
            startYRef.current = e.touches[0].clientY;
            isDraggingRef.current = false;
        };

        const onTouchMove = (e: TouchEvent) => {
            if (isRefreshingRef.current || scrollParent.scrollTop > 0) return;
            const distance = e.touches[0].clientY - startYRef.current;
            if (distance <= 0) return;
            isDraggingRef.current = true;
            e.preventDefault();
            const capped = Math.min(distance * 0.5, MAX_PULL);
            pullDistanceRef.current = capped;
            setPullDistance(capped);
        };

        const onTouchEnd = async () => {
            if (!isDraggingRef.current) return;
            isDraggingRef.current = false;

            if (pullDistanceRef.current >= THRESHOLD && !isRefreshingRef.current) {
                isRefreshingRef.current = true;
                setIsRefreshing(true);
                pullDistanceRef.current = 0;
                setPullDistance(0);
                try {
                    await onRefresh();
                } finally {
                    isRefreshingRef.current = false;
                    setIsRefreshing(false);
                }
            } else {
                pullDistanceRef.current = 0;
                setPullDistance(0);
            }
        };

        scrollParent.addEventListener('touchstart', onTouchStart, { passive: true });
        scrollParent.addEventListener('touchmove', onTouchMove, { passive: false });
        scrollParent.addEventListener('touchend', onTouchEnd);

        return () => {
            scrollParent.removeEventListener('touchstart', onTouchStart);
            scrollParent.removeEventListener('touchmove', onTouchMove);
            scrollParent.removeEventListener('touchend', onTouchEnd);
        };
    }, [onRefresh]);

    return {
        containerRef,
        pullDistance,
        isRefreshing,
        threshold: THRESHOLD,
    };
}

export default usePullToRefresh;
